"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { gaEvent, hasAnalyticsConsent } from "@/lib/gtag";

/**
 * Tracks scroll depth (25/50/75/100) once per pathname and emits a
 * `time_on_page` GA4 event when the user leaves a page.
 */
export default function ScrollDepthTracker() {
  const pathname = usePathname();
  const firedRef = useRef<Set<number>>(new Set());
  const enteredAtRef = useRef<number>(Date.now());

  useEffect(() => {
    firedRef.current = new Set();
    enteredAtRef.current = Date.now();

    if (!hasAnalyticsConsent()) return;
    if (!pathname) return;
    if (pathname.startsWith("/admin") || pathname.startsWith("/api")) return;

    const thresholds = [25, 50, 75, 100];

    const computeMaxScrollPercent = () => {
      const doc = document.documentElement;
      const body = document.body;
      const scrollTop = window.scrollY || doc.scrollTop;
      const total = Math.max(doc.scrollHeight, body.scrollHeight) - window.innerHeight;
      if (total <= 0) return 100;
      return Math.min(100, Math.round((scrollTop / total) * 100));
    };

    const onScroll = () => {
      const pct = computeMaxScrollPercent();
      for (const t of thresholds) {
        if (pct >= t && !firedRef.current.has(t)) {
          firedRef.current.add(t);
          gaEvent("scroll_depth", { percent: t, path: pathname });
        }
      }
    };

    const onUnload = () => {
      const secs = Math.round((Date.now() - enteredAtRef.current) / 1000);
      if (secs > 1) {
        gaEvent("time_on_page", { seconds: secs, path: pathname });
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("beforeunload", onUnload);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") onUnload();
    });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("beforeunload", onUnload);
    };
  }, [pathname]);

  return null;
}
